import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import Cookies from 'js-cookie';
import { toast } from 'sonner';
import { PaymentButton, PaymentSecurityBadge, ProductDownloads } from './PaymentComponents';

// Shadcn UI Components
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Textarea } from './components/ui/textarea';
import { Badge } from './components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';

// Icons
import { 
  Apple, Plus, Trash2, DollarSign, Clock, Target, Activity,
  ShoppingCart, Lock, Unlock, Star, ChefHat, Utensils, Timer,
  Calculator, CheckCircle, Calendar, Camera, X, Loader2,
  ChevronDown, ChevronUp, Flame, Leaf, ImageIcon, CheckCircle2, Eye, Download, Bookmark
} from 'lucide-react';

import { BACKEND_URL, API } from './config';

// Macro donut chart — pure CSS, no chart library needed
const MacroChart = ({ protein = 0, carbs = 0, fats = 0, calories = 0 }) => {
  const total = protein + carbs + fats;
  if (total === 0) return null;
  const pPct = Math.round((protein / total) * 100);
  const cPct = Math.round((carbs / total) * 100);
  const fPct = 100 - pPct - cPct;
  const calcCal = (protein * 4) + (carbs * 4) + (fats * 9);
  const displayCal = calories || calcCal;
  const pDeg = (pPct / 100) * 360;
  const cDeg = (cPct / 100) * 360;
  return (
    <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg border border-gray-100" data-testid="macro-chart">
      <div className="relative w-16 h-16 shrink-0">
        <div
          className="w-16 h-16 rounded-full"
          style={{
            background: `conic-gradient(#3b82f6 0deg ${pDeg}deg, #f59e0b ${pDeg}deg ${pDeg + cDeg}deg, #ef4444 ${pDeg + cDeg}deg 360deg)`
          }}
        />
        <div className="absolute inset-[6px] bg-white rounded-full flex items-center justify-center">
          <span className="text-[10px] font-bold text-gray-800">{displayCal}</span>
        </div>
      </div>
      <div className="flex-1 grid grid-cols-3 gap-2 text-center">
        <div>
          <div className="flex items-center justify-center gap-1">
            <div className="w-2 h-2 rounded-full bg-blue-500" />
            <span className="text-[10px] font-semibold text-gray-600">Protein</span>
          </div>
          <p className="text-sm font-bold text-gray-900">{protein}g</p>
          <p className="text-[10px] text-gray-400">{pPct}%</p>
        </div>
        <div>
          <div className="flex items-center justify-center gap-1">
            <div className="w-2 h-2 rounded-full bg-amber-500" />
            <span className="text-[10px] font-semibold text-gray-600">Carbs</span>
          </div>
          <p className="text-sm font-bold text-gray-900">{carbs}g</p>
          <p className="text-[10px] text-gray-400">{cPct}%</p>
        </div>
        <div>
          <div className="flex items-center justify-center gap-1">
            <div className="w-2 h-2 rounded-full bg-red-500" />
            <span className="text-[10px] font-semibold text-gray-600">Fats</span>
          </div>
          <p className="text-sm font-bold text-gray-900">{fats}g</p>
          <p className="text-[10px] text-gray-400">{fPct}%</p>
        </div>
      </div>
    </div>
  );
};

// Meal/nutrition-relevant niche options
const MEAL_NICHES = [
  { id: 'bodybuilding', label: 'Bodybuilding', color: '#DC2626' },
  { id: 'muscle_building', label: 'Muscle Building', color: '#B91C1C' },
  { id: 'weight_loss', label: 'Weight Loss', color: '#16A34A' },
  { id: 'endurance', label: 'Endurance / Running', color: '#0D9488' },
  { id: 'keto', label: 'Keto', color: '#F59E0B' },
  { id: 'vegan', label: 'Vegan / Plant-Based', color: '#22C55E' },
  { id: 'clean_eating', label: 'Clean Eating', color: '#10B981' },
  { id: 'meal_prep', label: 'Meal Prep', color: '#0891B2' },
  { id: 'high_protein', label: 'High Protein', color: '#DC2626' },
  { id: 'low_carb', label: 'Low Carb', color: '#EA580C' },
  { id: 'bulking', label: 'Bulking', color: '#7C3AED' },
  { id: 'cutting', label: 'Cutting / Leaning', color: '#E11D48' },
  { id: 'sports_nutrition', label: 'Sports Nutrition', color: '#0284C7' },
  { id: 'yoga', label: 'Yoga / Mindful Eating', color: '#8B5CF6' },
  { id: 'family_nutrition', label: 'Family / Budget', color: '#6366F1' },
  { id: 'gluten_free', label: 'Gluten-Free', color: '#D97706' },
];

const MealNicheSelector = ({ selected = [], onChange }) => (
  <div className="space-y-2">
    <label className="text-sm font-medium">Nutrition Categories (helps discovery)</label>
    <div className="flex flex-wrap gap-1.5">
      {MEAL_NICHES.map(n => {
        const active = selected.includes(n.id);
        return (
          <button
            key={n.id}
            type="button"
            onClick={() => onChange(active ? selected.filter(x => x !== n.id) : [...selected, n.id])}
            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
              active ? 'text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            style={active ? { backgroundColor: n.color } : {}}
            data-testid={`meal-niche-chip-${n.id}`}
          >
            {n.label}
          </button>
        );
      })}
    </div>
    {selected.length > 0 && (
      <p className="text-xs text-gray-500">{selected.length} selected</p>
    )}
  </div>
);

// Media URL resolver
const resolveMediaUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/')) return BACKEND_URL + url;
  return url;
};

// =============================================================================
// MEAL MEDIA LIGHTBOX (full-size photo/video viewer — portaled to body)
// =============================================================================
const MealMediaLightbox = ({ src, type = 'photo', alt = 'Meal', open, onClose }) => {
  // Handle Escape key to close lightbox (prevents parent Dialog from getting it)
  React.useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKey, true); // capture phase
    return () => document.removeEventListener('keydown', handleKey, true);
  }, [open, onClose]);

  if (!open || !src) return null;
  const fullSrc = resolveMediaUrl(src);

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/95 transition-opacity duration-200"
      onClick={onClose}
      data-testid="meal-media-lightbox"
    >
      <button onClick={onClose} className="absolute top-4 right-4 text-white/70 hover:text-white rounded-full hover:bg-white/10 p-2 z-10" data-testid="meal-lightbox-close">
        <X className="h-7 w-7" />
      </button>
      <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: '92vw', maxHeight: '88vh' }}>
        {type === 'video' ? (
          <video src={fullSrc} controls autoPlay className="max-w-[92vw] max-h-[85vh] rounded" style={{ objectFit: 'contain' }} data-testid="meal-lightbox-video" />
        ) : (
          <img src={fullSrc} alt={alt} className="max-w-[92vw] max-h-[85vh] object-contain rounded select-none" style={{ imageRendering: 'auto' }} draggable={false} data-testid="meal-lightbox-image" />
        )}
        <div className="flex items-center justify-center gap-4 mt-3">
          <a href={fullSrc} download className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-full text-white/80 text-xs transition-colors">
            <Download className="h-3.5 w-3.5" /> Download
          </a>
        </div>
      </div>
    </div>,
    document.body
  );
};

// =============================================================================
// MEAL PHOTO UPLOAD (reusable per-meal)
// =============================================================================
const MealPhotoUpload = ({ value, onChange, mealIndex }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Photo must be under 10MB'); return; }
    setUploading(true);
    try {
      const sessionToken = Cookies.get('session_token');
      const fd = new FormData();
      fd.append('file', file);
      const resp = await axios.post(`${API}/upload/media?category=meal-photo`, fd, {
        headers: { Authorization: `Bearer ${sessionToken}`, 'Content-Type': 'multipart/form-data' }
      });
      onChange(resp.data.url);
      toast.success('Photo uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Photo upload failed');
    } finally {
      setUploading(false);
    }
  };

  const resolved = resolveMediaUrl(value);

  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-gray-500">Meal Photo</label>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleUpload} data-testid={`meal-photo-input-${mealIndex}`} />
      {value ? (
        <div className="relative group rounded-lg overflow-hidden h-28 border border-gray-200">
          <img src={resolved} alt="Meal" className="w-full h-full object-cover" />
          <button onClick={() => onChange(null)} className="absolute top-1.5 right-1.5 bg-black/60 rounded-full p-1 hover:bg-red-600 transition opacity-0 group-hover:opacity-100">
            <X className="h-3 w-3 text-white" />
          </button>
        </div>
      ) : uploading ? (
        <div className="flex items-center justify-center h-20 bg-green-50 border border-green-200 rounded-lg">
          <Loader2 className="h-5 w-5 text-green-600 animate-spin" />
        </div>
      ) : (
        <button type="button" onClick={() => inputRef.current?.click()} className="w-full flex items-center gap-2 p-2.5 border border-dashed border-gray-300 rounded-lg hover:border-green-400 hover:bg-green-50/40 transition-all" data-testid={`meal-photo-upload-${mealIndex}`}>
          <Camera className="h-4 w-4 text-gray-400" />
          <span className="text-xs text-gray-500">Add meal photo (JPEG, PNG &middot; max 10MB)</span>
        </button>
      )}
    </div>
  );
};


// =============================================================================
// VIDEO UPLOAD COMPONENT (reusable per-meal, same pattern as workout plans)
// =============================================================================
const MealVideoUpload = ({ value, onChange, mealIndex }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) { toast.error('Video must be under 50MB'); return; }
    setUploading(true);
    setProgress(0);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const sessionToken = Cookies.get('session_token');
      const resp = await axios.post(`${API}/upload/media?category=exercise-video`, formData, {
        headers: { Authorization: `Bearer ${sessionToken}`, 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => { if (e.total) setProgress(Math.round((e.loaded / e.total) * 100)); }
      });
      onChange(resp.data.url);
      toast.success('Video uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Video upload failed');
    } finally { setUploading(false); setProgress(0); }
  };

  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-gray-500">Recipe Video (primary media)</label>
      <input ref={inputRef} type="file" accept="video/mp4,video/quicktime,video/webm,video/x-msvideo" className="hidden" onChange={handleUpload} data-testid={`meal-video-input-${mealIndex}`} />
      {value ? (
        <div className="flex items-center gap-2 p-2 bg-emerald-50 border border-emerald-200 rounded-lg">
          <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
          <span className="text-xs text-emerald-700 truncate flex-1">Video attached</span>
          <button onClick={() => onChange(null)} className="text-gray-400 hover:text-red-500 transition-colors"><X className="h-3.5 w-3.5" /></button>
        </div>
      ) : uploading ? (
        <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
          <Loader2 className="h-4 w-4 text-blue-600 animate-spin shrink-0" />
          <div className="flex-1"><div className="h-1.5 bg-blue-100 rounded-full overflow-hidden"><div className="h-full bg-blue-500 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} /></div></div>
          <span className="text-xs text-blue-600">{progress}%</span>
        </div>
      ) : (
        <button type="button" onClick={() => inputRef.current?.click()} className="w-full flex items-center gap-2 p-2 border border-dashed border-gray-300 rounded-lg hover:border-emerald-400 hover:bg-emerald-50/50 transition-all text-left" data-testid={`meal-video-upload-${mealIndex}`}>
          <Activity className="h-4 w-4 text-gray-400" />
          <span className="text-xs text-gray-500">Add recipe video (MP4, MOV &middot; max 50MB)</span>
        </button>
      )}
    </div>
  );
};


// =============================================================================
// CREATE MEAL PLAN DIALOG
// =============================================================================
export const CreateMealPlanDialog = ({ open, onClose, onSuccess, editPlan = null }) => {
  const buildFormData = (plan) => {
    if (!plan) return {
      title: '', description: '', price: '', audience: 'public', pricing: 'one_time_purchase',
      duration_days: 7, dietary_restrictions: [], target_goals: [], daily_calories: '',
      daily_macros: { protein: '', carbs: '', fats: '' }, fitness_niches: [],
      meals: [{ name: '', ingredients: [], instructions: '', calories: '', macros: { protein: '', carbs: '', fats: '' }, meal_type: 'breakfast', video_url: null, photo_url: null, prep_time: '' }]
    };
    return {
      title: plan.title || '',
      description: plan.description || '',
      price: plan.price ? String(plan.price) : '',
      audience: plan.audience || 'public',
      pricing: plan.pricing || (plan.price > 0 ? 'one_time_purchase' : 'free'),
      duration_days: plan.duration_days || 7,
      dietary_restrictions: plan.dietary_restrictions || [],
      target_goals: plan.target_goals || [],
      daily_calories: plan.daily_calories ? String(plan.daily_calories) : '',
      daily_macros: {
        protein: plan.daily_macros?.protein ? String(plan.daily_macros.protein) : '',
        carbs: plan.daily_macros?.carbs ? String(plan.daily_macros.carbs) : '',
        fats: plan.daily_macros?.fats ? String(plan.daily_macros.fats) : ''
      },
      fitness_niches: plan.fitness_niches || [],
      meals: (plan.meals && plan.meals.length > 0) ? plan.meals.map(m => ({
        name: m.name || '', ingredients: m.ingredients || [], instructions: m.instructions || '',
        calories: m.calories ? String(m.calories) : '',
        macros: { protein: m.macros?.protein ? String(m.macros.protein) : '', carbs: m.macros?.carbs ? String(m.macros.carbs) : '', fats: m.macros?.fats ? String(m.macros.fats) : '' },
        meal_type: m.meal_type || 'breakfast', video_url: m.video_url || null, photo_url: m.photo_url || null, prep_time: m.prep_time || ''
      })) : [{ name: '', ingredients: [], instructions: '', calories: '', macros: { protein: '', carbs: '', fats: '' }, meal_type: 'breakfast', video_url: null, photo_url: null, prep_time: '' }]
    };
  };

  const [formData, setFormData] = useState(buildFormData(null));
  const [loading, setLoading] = useState(false);
  const isEditMode = !!editPlan;

  React.useEffect(() => {
    if (open) setFormData(buildFormData(editPlan));
  }, [open, editPlan]);

  const addMeal = () => {
    setFormData({
      ...formData,
      meals: [...formData.meals, { name: '', ingredients: [], instructions: '', calories: '', macros: { protein: '', carbs: '', fats: '' }, meal_type: 'breakfast', video_url: null, photo_url: null, prep_time: '' }]
    });
  };

  const removeMeal = (index) => {
    setFormData({ ...formData, meals: formData.meals.filter((_, i) => i !== index) });
  };

  const updateMeal = (index, field, value) => {
    const newMeals = [...formData.meals];
    if (field === 'ingredients') {
      const parts = value.split(',');
      const processed = parts.map((item, i) => i < parts.length - 1 ? item.trim() : item).filter((item, i) => i === parts.length - 1 || item);
      newMeals[index] = { ...newMeals[index], [field]: processed };
    } else if (field.startsWith('macros.')) {
      const macroField = field.split('.')[1];
      newMeals[index] = { ...newMeals[index], macros: { ...newMeals[index].macros, [macroField]: value } };
    } else {
      newMeals[index] = { ...newMeals[index], [field]: value };
    }
    setFormData({ ...formData, meals: newMeals });
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.description) {
      toast.error('Please fill in title and description');
      return;
    }
    if (formData.pricing === 'one_time_purchase' && (!formData.price || parseFloat(formData.price) <= 0)) {
      toast.error('Please set a price for one-time purchase products');
      return;
    }
    if (formData.meals.some(meal => !meal.name || !meal.instructions)) {
      toast.error('Please complete all meal information');
      return;
    }
    setLoading(true);
    try {
      const sessionToken = Cookies.get('session_token');
      const submitData = {
        title: formData.title,
        description: formData.description,
        price: formData.pricing === 'free' ? 0 : parseFloat(formData.price),
        audience: formData.audience,
        pricing: formData.pricing,
        duration_days: formData.duration_days,
        dietary_restrictions: (formData.dietary_restrictions || []).map(s => s.trim()).filter(Boolean),
        target_goals: (formData.target_goals || []).map(s => s.trim()).filter(Boolean),
        daily_calories: formData.daily_calories ? parseInt(formData.daily_calories) : null,
        daily_macros: {
          protein: formData.daily_macros.protein ? parseFloat(formData.daily_macros.protein) : null,
          carbs: formData.daily_macros.carbs ? parseFloat(formData.daily_macros.carbs) : null,
          fats: formData.daily_macros.fats ? parseFloat(formData.daily_macros.fats) : null
        },
        meals: formData.meals.map(meal => ({
          name: meal.name,
          ingredients: (meal.ingredients || []).map(s => s.trim()).filter(Boolean),
          instructions: meal.instructions,
          calories: meal.calories ? parseInt(meal.calories) : null,
          macros: {
            protein: meal.macros?.protein ? parseFloat(meal.macros.protein) : null,
            carbs: meal.macros?.carbs ? parseFloat(meal.macros.carbs) : null,
            fats: meal.macros?.fats ? parseFloat(meal.macros.fats) : null
          },
          meal_type: meal.meal_type,
          video_url: meal.video_url,
          photo_url: meal.photo_url,
          prep_time: meal.prep_time || null
        }))
      };
      if (isEditMode) {
        await axios.put(`${API}/meal-plans/${editPlan.id}`, submitData, {
          headers: { Authorization: `Bearer ${sessionToken}` }
        });
        toast.success('Meal plan updated!');
      } else {
        await axios.post(`${API}/meal-plans`, submitData, {
          headers: { Authorization: `Bearer ${sessionToken}` }
        });
        toast.success('Meal plan created successfully!');
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} meal plan:`, error);
      const detail = error.response?.data?.detail || `Failed to ${isEditMode ? 'update' : 'create'} meal plan`;
      toast.error(typeof detail === 'string' ? detail : `Failed to ${isEditMode ? 'update' : 'create'} meal plan`);
    } finally {
      setLoading(false);
    }
  };

  const getAccessSummary = () => {
    const { audience, pricing } = formData;
    if (audience === 'public' && pricing === 'free') return 'Anyone can access this plan for free.';
    if (audience === 'public' && pricing === 'one_time_purchase') return 'Anyone can buy this plan with a one-time payment.';
    if (audience === 'subscribers' && pricing === 'free') return 'Only your subscribers can access this plan (included with subscription).';
    if (audience === 'subscribers' && pricing === 'one_time_purchase') return 'Only subscribers can buy this plan — subscription + one-time payment required.';
    return '';
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" onKeyDown={(e) => {
        const tag = e.target?.tagName;
        if ((tag === 'INPUT' || tag === 'TEXTAREA') && e.key === ' ') e.stopPropagation();
      }}>
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Apple className="h-5 w-5 text-green-600" />
            <span>{isEditMode ? 'Edit Meal Plan' : 'Create Meal Plan'}</span>
          </DialogTitle>
          <DialogDescription>{isEditMode ? 'Update your meal plan details and meals' : 'Create a premium nutrition plan with per-meal videos and detailed macros'}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Basic Info */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Plan Title *</label>
            <Input placeholder="e.g., 7-Day Keto Meal Plan" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} data-testid="meal-title-input" />
          </div>

          {/* Audience + Pricing — 2 selectors */}
          <div className="p-4 rounded-xl border-2 border-green-100 bg-green-50/30 space-y-3">
            <div className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <Lock className="h-4 w-4 text-green-600" />
              Access &amp; Pricing
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-gray-600">Who can access?</label>
                <Select value={formData.audience} onValueChange={(value) => setFormData({ ...formData, audience: value })} data-testid="meal-audience-select">
                  <SelectTrigger data-testid="meal-audience-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="public">Public — Everyone</SelectItem>
                    <SelectItem value="subscribers">Subscribers Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-gray-600">How do they pay?</label>
                <Select value={formData.pricing} onValueChange={(value) => setFormData({ ...formData, pricing: value })} data-testid="meal-pricing-select">
                  <SelectTrigger data-testid="meal-pricing-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="one_time_purchase">One-Time Purchase</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {formData.pricing === 'one_time_purchase' && (
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-gray-600">Price ($) *</label>
                <Input type="number" placeholder="19.99" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} data-testid="meal-price-input" className="max-w-[200px]" />
              </div>
            )}
            <p className="text-xs text-green-700 bg-green-100/50 rounded-lg px-3 py-2" data-testid="meal-access-summary">{getAccessSummary()}</p>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Description *</label>
            <Textarea placeholder="Describe your meal plan, who it's for, and what benefits they can expect..." value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} data-testid="meal-description-input" />
          </div>

          <MealNicheSelector
            selected={formData.fitness_niches}
            onChange={(v) => setFormData({ ...formData, fitness_niches: v })}
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Duration (days)</label>
              <Input type="number" value={formData.duration_days} onChange={(e) => setFormData({ ...formData, duration_days: parseInt(e.target.value) || 1 })} />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Daily Calories</label>
              <Input type="number" placeholder="2000" value={formData.daily_calories} onChange={(e) => setFormData({ ...formData, daily_calories: e.target.value })} />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Daily Protein (g)</label>
              <Input type="number" placeholder="120" value={formData.daily_macros.protein} onChange={(e) => setFormData({ ...formData, daily_macros: { ...formData.daily_macros, protein: e.target.value } })} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Dietary Restrictions</label>
              <Input placeholder="Vegetarian, Vegan, Keto (comma separated)" value={formData.dietary_restrictions.join(', ')} onChange={(e) => { const parts = e.target.value.split(','); setFormData({ ...formData, dietary_restrictions: parts.map((item, i) => i < parts.length - 1 ? item.trim() : item).filter((item, i) => i === parts.length - 1 || item) }); }} />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Target Goals</label>
              <Input placeholder="Weight Loss, Muscle Gain (comma separated)" value={formData.target_goals.join(', ')} onChange={(e) => { const parts = e.target.value.split(','); setFormData({ ...formData, target_goals: parts.map((item, i) => i < parts.length - 1 ? item.trim() : item).filter((item, i) => i === parts.length - 1 || item) }); }} />
            </div>
          </div>

          {/* Meals */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Meals</h3>
              <Button onClick={addMeal} size="sm" variant="outline" data-testid="add-meal-btn">
                <Plus className="h-4 w-4 mr-1" />Add Meal
              </Button>
            </div>

            <div className="space-y-4">
              {formData.meals.map((meal, index) => (
                <Card key={index} className="border-green-100">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-sm text-gray-900">Meal {index + 1}</h4>
                      {formData.meals.length > 1 && (
                        <Button onClick={() => removeMeal(index)} size="sm" variant="ghost" className="text-red-500 hover:text-red-600 h-7 w-7 p-0">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-3 gap-2">
                      <div className="col-span-2">
                        <Input placeholder="Meal name (e.g., Protein Smoothie)" value={meal.name} onChange={(e) => updateMeal(index, 'name', e.target.value)} data-testid={`meal-name-${index}`} />
                      </div>
                      <Select value={meal.meal_type} onValueChange={(value) => updateMeal(index, 'meal_type', value)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="breakfast">Breakfast</SelectItem>
                          <SelectItem value="lunch">Lunch</SelectItem>
                          <SelectItem value="dinner">Dinner</SelectItem>
                          <SelectItem value="snack">Snack</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <Input placeholder="Ingredients (comma separated)" value={meal.ingredients.join(', ')} onChange={(e) => updateMeal(index, 'ingredients', e.target.value)} />

                    <Textarea placeholder="Preparation instructions..." value={meal.instructions} onChange={(e) => updateMeal(index, 'instructions', e.target.value)} rows={2} />

                    <div className="grid grid-cols-5 gap-2">
                      <Input type="number" placeholder="Calories" value={meal.calories} onChange={(e) => updateMeal(index, 'calories', e.target.value)} data-testid={`meal-calories-${index}`} />
                      <Input type="number" placeholder="Protein (g)" value={meal.macros.protein} onChange={(e) => updateMeal(index, 'macros.protein', e.target.value)} data-testid={`meal-protein-${index}`} />
                      <Input type="number" placeholder="Carbs (g)" value={meal.macros.carbs} onChange={(e) => updateMeal(index, 'macros.carbs', e.target.value)} data-testid={`meal-carbs-${index}`} />
                      <Input type="number" placeholder="Fats (g)" value={meal.macros.fats} onChange={(e) => updateMeal(index, 'macros.fats', e.target.value)} data-testid={`meal-fats-${index}`} />
                      <Input placeholder="Prep time" value={meal.prep_time} onChange={(e) => updateMeal(index, 'prep_time', e.target.value)} />
                    </div>

                    {/* Extended nutrition fields */}
                    <div className="grid grid-cols-4 gap-2">
                      <Input type="number" placeholder="Fiber (g)" value={meal.fiber || ''} onChange={(e) => updateMeal(index, 'fiber', e.target.value)} />
                      <Input type="number" placeholder="Sugar (g)" value={meal.sugar || ''} onChange={(e) => updateMeal(index, 'sugar', e.target.value)} />
                      <Input type="number" placeholder="Sat. Fat (g)" value={meal.saturated_fat || ''} onChange={(e) => updateMeal(index, 'saturated_fat', e.target.value)} />
                      <Input type="number" placeholder="Sodium (mg)" value={meal.sodium || ''} onChange={(e) => updateMeal(index, 'sodium', e.target.value)} />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input placeholder="Serving size (e.g., 1 cup)" value={meal.serving_size || ''} onChange={(e) => updateMeal(index, 'serving_size', e.target.value)} />
                      <Input placeholder="Notes (optional)" value={meal.notes || ''} onChange={(e) => updateMeal(index, 'notes', e.target.value)} />
                    </div>

                    {/* Macro donut chart */}
                    <MacroChart protein={parseFloat(meal.macros.protein) || 0} carbs={parseFloat(meal.macros.carbs) || 0} fats={parseFloat(meal.macros.fats) || 0} calories={parseInt(meal.calories) || 0} />

                    {/* Per-meal video upload (PRIMARY) */}
                    <MealVideoUpload value={meal.video_url} onChange={(url) => updateMeal(index, 'video_url', url)} mealIndex={index} />
                    {/* Per-meal photo upload (OPTIONAL fallback) */}
                    <MealPhotoUpload value={meal.photo_url} onChange={(url) => updateMeal(index, 'photo_url', url)} mealIndex={index} />
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Submit */}
          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={loading} className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white" data-testid="create-meal-submit">
              {loading ? (isEditMode ? 'Saving...' : 'Creating...') : (isEditMode ? 'Save Changes' : 'Create Meal Plan')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};


// =============================================================================
// MEAL PLAN DETAIL VIEW
// =============================================================================
export const MealPlanDetailView = ({ plan, open, onClose }) => {
  const [expandedMeal, setExpandedMeal] = useState(null);
  const [lightbox, setLightbox] = useState({ open: false, src: null, type: 'photo', alt: '' });

  // Reset all local state when dialog opens/closes
  React.useEffect(() => {
    if (!open) {
      setLightbox({ open: false, src: null, type: 'photo', alt: '' });
      setExpandedMeal(null);
    }
  }, [open]);

  if (!plan) return null;

  const mealsWithMedia = (plan.meals || []).filter(m => m.video_url || m.photo_url);
  const mealsWithVideos = (plan.meals || []).filter(m => m.video_url);
  const mealsWithPhotos = (plan.meals || []).filter(m => m.photo_url);
  const totalMealCalories = (plan.meals || []).reduce((sum, m) => sum + (m.calories || 0), 0);
  const { audience, pricing } = getAccessModel(plan);

  const handleDialogOpenChange = (isOpen) => {
    if (!isOpen && lightbox.open) {
      // Escape or outside-click while lightbox is showing → close lightbox only
      setLightbox({ open: false, src: null, type: 'photo', alt: '' });
      return;
    }
    if (!isOpen) {
      setLightbox({ open: false, src: null, type: 'photo', alt: '' });
      setExpandedMeal(null);
    }
    onClose();
  };

  const getMealTypeStyle = (type) => {
    switch (type) {
      case 'breakfast': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'lunch': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'dinner': return 'bg-violet-100 text-violet-700 border-violet-200';
      case 'snack': return 'bg-pink-100 text-pink-700 border-pink-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getMealTypeIcon = (type) => {
    switch (type) {
      case 'breakfast': return '🌅';
      case 'lunch': return '☀️';
      case 'dinner': return '🌙';
      case 'snack': return '🍎';
      default: return '🍽️';
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto p-0" data-testid="meal-detail-view">
        {/* Hero header */}
        <div className="bg-gradient-to-br from-emerald-900 via-green-950 to-emerald-900 text-white p-6 pb-8 rounded-t-lg">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-3 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                {(plan.dietary_restrictions || []).slice(0, 2).map((r, i) => (
                  <Badge key={i} className="bg-white/15 text-green-100 border-green-400/30 border text-xs px-2.5 py-0.5">
                    <Leaf className="h-3 w-3 mr-0.5" />{r}
                  </Badge>
                ))}
                {pricing === 'one_time_purchase' && (
                  <Badge className="bg-green-500/20 text-green-200 border-green-400/30 border text-xs px-2.5 py-0.5">
                    <DollarSign className="h-3 w-3 mr-0.5" />${plan.price}
                  </Badge>
                )}
                {pricing === 'free' && (
                  <Badge className="bg-emerald-500/20 text-emerald-200 border-emerald-400/30 border text-xs px-2.5 py-0.5">Free</Badge>
                )}
                {audience === 'subscribers' && (
                  <Badge className="bg-amber-500/20 text-amber-200 border-amber-400/30 border text-xs px-2.5 py-0.5">
                    <Lock className="h-3 w-3 mr-0.5" />Subscribers
                  </Badge>
                )}
              </div>
              <h2 className="text-2xl font-bold leading-tight" data-testid="meal-detail-title">{plan.title}</h2>
              <p className="text-sm text-green-200/80">by {plan.creator_name}</p>
            </div>
            <Apple className="h-10 w-10 text-green-400/40 shrink-0" />
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
            <div className="bg-white/10 rounded-lg p-3 text-center backdrop-blur-sm">
              <Calendar className="h-5 w-5 text-green-300 mx-auto mb-1" />
              <div className="text-lg font-bold">{plan.duration_days}</div>
              <div className="text-xs text-green-300/70">Days</div>
            </div>
            <div className="bg-white/10 rounded-lg p-3 text-center backdrop-blur-sm">
              <Utensils className="h-5 w-5 text-amber-300 mx-auto mb-1" />
              <div className="text-lg font-bold">{(plan.meals || []).length}</div>
              <div className="text-xs text-green-300/70">Meals</div>
            </div>
            {(plan.daily_calories || totalMealCalories > 0) && (
              <div className="bg-white/10 rounded-lg p-3 text-center backdrop-blur-sm">
                <Flame className="h-5 w-5 text-red-300 mx-auto mb-1" />
                <div className="text-lg font-bold">{plan.daily_calories || totalMealCalories}</div>
                <div className="text-xs text-green-300/70">Cal/Day</div>
              </div>
            )}
            {mealsWithMedia.length > 0 && (
              <div className="bg-white/10 rounded-lg p-3 text-center backdrop-blur-sm">
                <Activity className="h-5 w-5 text-violet-300 mx-auto mb-1" />
                <div className="text-lg font-bold">{mealsWithMedia.length}</div>
                <div className="text-xs text-green-300/70">Media</div>
              </div>
            )}
          </div>

          {/* Daily macros bar */}
          {plan.daily_macros && (plan.daily_macros.protein || plan.daily_macros.carbs || plan.daily_macros.fats) && (
            <div className="flex items-center gap-4 mt-4 pt-4 border-t border-white/10">
              <span className="text-xs text-green-300/60 uppercase tracking-wider">Daily Macros</span>
              <div className="flex gap-3 flex-1">
                {plan.daily_macros.protein && <div className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-500/20 rounded-full"><span className="text-xs font-medium text-blue-200">{plan.daily_macros.protein}g Protein</span></div>}
                {plan.daily_macros.carbs && <div className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-500/20 rounded-full"><span className="text-xs font-medium text-amber-200">{plan.daily_macros.carbs}g Carbs</span></div>}
                {plan.daily_macros.fats && <div className="flex items-center gap-1.5 px-2.5 py-1 bg-red-500/20 rounded-full"><span className="text-xs font-medium text-red-200">{plan.daily_macros.fats}g Fats</span></div>}
              </div>
            </div>
          )}
        </div>

        <div className="p-6 space-y-6">
          {/* Description */}
          {plan.description && <p className="text-gray-700 leading-relaxed" data-testid="meal-detail-description">{plan.description}</p>}

          {/* Dietary + Goals */}
          <div className="grid md:grid-cols-2 gap-4">
            {(plan.dietary_restrictions || []).length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-gray-900">Dietary Info</h4>
                <div className="flex flex-wrap gap-1.5">
                  {plan.dietary_restrictions.map((r, i) => <Badge key={i} variant="outline" className="text-xs border-green-300 text-green-700">{r}</Badge>)}
                </div>
              </div>
            )}
            {(plan.target_goals || []).length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-gray-900">Target Goals</h4>
                <div className="flex flex-wrap gap-1.5">
                  {plan.target_goals.map((g, i) => <Badge key={i} className="bg-green-50 text-green-700 border-green-200 border text-xs">{g}</Badge>)}
                </div>
              </div>
            )}
          </div>

          {/* Meals List */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <Utensils className="h-4 w-4 text-green-600" />
              Meals ({(plan.meals || []).length})
            </h4>

            <div className="space-y-2">
              {(plan.meals || []).map((meal, idx) => {
                const isExpanded = expandedMeal === idx;
                const photoUrl = resolveMediaUrl(meal.photo_url);
                return (
                  <div key={idx} className={`border rounded-xl overflow-hidden transition-all duration-200 ${isExpanded ? 'border-green-300 shadow-sm' : 'border-gray-200 hover:border-gray-300'}`} data-testid={`meal-detail-${idx}`}>
                    {/* Meal header */}
                    <button onClick={() => setExpandedMeal(isExpanded ? null : idx)} className="w-full flex items-center gap-3 p-4 text-left hover:bg-gray-50/50 transition-colors">
                      {/* Thumbnail: prioritize video indicator, fallback to photo, then icon */}
                      {meal.video_url ? (
                        <div className="w-12 h-12 rounded-lg bg-gray-900 flex items-center justify-center shrink-0 border border-gray-300 relative group">
                          <svg className="h-5 w-5 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                          <div className="absolute inset-0 bg-green-500/20 rounded-lg" />
                        </div>
                      ) : photoUrl ? (
                        <img src={photoUrl} alt={meal.name} className="w-12 h-12 rounded-lg object-cover shrink-0 border border-gray-200 hover:ring-2 hover:ring-green-300 transition-all cursor-pointer" onClick={(e) => { e.stopPropagation(); setLightbox({ open: true, src: meal.photo_url, type: 'photo', alt: meal.name }); }} data-testid={`meal-thumb-${idx}`} />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-green-50 flex items-center justify-center text-lg shrink-0 border border-green-100">
                          {getMealTypeIcon(meal.meal_type)}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">{meal.name || `Meal ${idx + 1}`}</div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Badge className={`${getMealTypeStyle(meal.meal_type)} text-xs px-1.5 py-0 capitalize border`}>{meal.meal_type}</Badge>
                          {meal.calories && <span className="text-xs text-gray-500">{meal.calories} cal</span>}
                          {meal.prep_time && <span className="text-xs text-gray-500 flex items-center gap-0.5"><Timer className="h-3 w-3" />{meal.prep_time}</span>}
                          {(meal.photo_url || meal.video_url) && (
                            <span className="text-xs text-green-500 flex items-center gap-1">
                              {meal.photo_url && <Camera className="h-3 w-3" />}
                              {meal.video_url && <Eye className="h-3 w-3" />}
                              Media
                            </span>
                          )}
                        </div>
                      </div>
                      {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                    </button>

                    {/* Expanded content */}
                    {isExpanded && (
                      <div className="px-4 pb-4 space-y-4 border-t border-gray-100 pt-3">
                        {/* Video — PRIMARY media, click to play in lightbox */}
                        {meal.video_url && (
                          <div className="space-y-2">
                            <div className="rounded-xl overflow-hidden border border-gray-200 bg-gray-900 relative group cursor-pointer" style={{ maxHeight: '22rem' }} onClick={() => setLightbox({ open: true, src: meal.video_url, type: 'video', alt: meal.name })} data-testid={`meal-video-view-${idx}`}>
                              <video src={resolveMediaUrl(meal.video_url)} className="w-full h-full object-cover" muted playsInline preload="metadata" />
                              <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                                <div className="bg-white/90 backdrop-blur-sm rounded-full w-16 h-16 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                                  <svg className="h-7 w-7 text-green-700 ml-0.5" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                                </div>
                              </div>
                              <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                                <div className="bg-white/90 backdrop-blur-sm rounded-full px-3 py-1.5 flex items-center gap-1.5 shadow-lg">
                                  <Eye className="h-3.5 w-3.5 text-green-700" />
                                  <span className="text-xs font-medium text-green-800">Play Full Video</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Photo — fallback/secondary (only shown if no video, or alongside video) */}
                        {photoUrl && (
                          <div className="space-y-2">
                            <div className="rounded-xl overflow-hidden border border-gray-200 cursor-pointer group relative" style={{ maxHeight: meal.video_url ? '12rem' : '20rem' }} onClick={() => setLightbox({ open: true, src: meal.photo_url, type: 'photo', alt: meal.name })} data-testid={`meal-photo-view-${idx}`}>
                              <img src={photoUrl} alt={meal.name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-4">
                                <div className="bg-white/90 backdrop-blur-sm rounded-full px-4 py-2 flex items-center gap-2 shadow-lg">
                                  <Eye className="h-4 w-4 text-green-700" />
                                  <span className="text-sm font-medium text-green-800">View Full Size</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Macros pills + chart */}
                        {meal.macros && (meal.macros.protein || meal.macros.carbs || meal.macros.fats) && (
                          <div className="space-y-2">
                            <div className="flex flex-wrap gap-2">
                              {meal.calories && <div className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 rounded-full"><Flame className="h-3.5 w-3.5 text-red-600" /><span className="text-xs font-medium text-red-700">{meal.calories} cal</span></div>}
                              {meal.macros.protein && <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 rounded-full"><Target className="h-3.5 w-3.5 text-blue-600" /><span className="text-xs font-medium text-blue-700">{meal.macros.protein}g protein</span></div>}
                              {meal.macros.carbs && <div className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 rounded-full"><Activity className="h-3.5 w-3.5 text-amber-600" /><span className="text-xs font-medium text-amber-700">{meal.macros.carbs}g carbs</span></div>}
                              {meal.macros.fats && <div className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-50 rounded-full"><span className="text-xs font-medium text-violet-700">{meal.macros.fats}g fats</span></div>}
                            </div>
                            <MacroChart protein={parseFloat(meal.macros.protein) || 0} carbs={parseFloat(meal.macros.carbs) || 0} fats={parseFloat(meal.macros.fats) || 0} calories={parseInt(meal.calories) || 0} />
                          </div>
                        )}

                        {/* Extended nutrition info */}
                        {(meal.fiber || meal.sugar || meal.saturated_fat || meal.sodium || meal.serving_size) && (
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                            {meal.serving_size && <span>Serving: <strong className="text-gray-700">{meal.serving_size}</strong></span>}
                            {meal.fiber && <span>Fiber: <strong className="text-gray-700">{meal.fiber}g</strong></span>}
                            {meal.sugar && <span>Sugar: <strong className="text-gray-700">{meal.sugar}g</strong></span>}
                            {meal.saturated_fat && <span>Sat. Fat: <strong className="text-gray-700">{meal.saturated_fat}g</strong></span>}
                            {meal.sodium && <span>Sodium: <strong className="text-gray-700">{meal.sodium}mg</strong></span>}
                          </div>
                        )}
                        {meal.notes && (
                          <p className="text-xs text-gray-500 italic">{meal.notes}</p>
                        )}

                        {/* Prep time */}
                        {meal.prep_time && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Timer className="h-4 w-4 text-green-600" />
                            <span>Prep time: <strong>{meal.prep_time}</strong></span>
                          </div>
                        )}

                        {/* Ingredients */}
                        {(meal.ingredients || []).length > 0 && (
                          <div className="bg-green-50/70 rounded-lg p-3">
                            <div className="text-xs font-semibold text-green-800 mb-2 uppercase tracking-wide flex items-center gap-1.5">
                              <ShoppingCart className="h-3.5 w-3.5" /> Ingredients
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {meal.ingredients.map((ing, i) => (
                                <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 bg-white rounded-full text-xs text-gray-700 border border-green-200">
                                  <CheckCircle2 className="h-3 w-3 text-green-500" />{ing}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Instructions */}
                        {meal.instructions && (
                          <div className="bg-gray-50 rounded-lg p-3">
                            <div className="text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Preparation</div>
                            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">{meal.instructions}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Product Downloads */}
          <ProductDownloads productType="meal_plan" productId={plan.id} />
        </div>
      </DialogContent>

      {/* Full-size media lightbox — rendered outside DialogContent to avoid pointer-down-outside interference */}
      <MealMediaLightbox
        src={lightbox.src}
        type={lightbox.type}
        alt={lightbox.alt}
        open={lightbox.open}
        onClose={() => setLightbox({ open: false, src: null, type: 'photo', alt: '' })}
      />
    </Dialog>
  );
};


// Helper: derive audience+pricing from plan data (handles both new and old records)
const getAccessModel = (plan) => {
  if (plan.audience && plan.pricing) return { audience: plan.audience, pricing: plan.pricing };
  const at = plan.access_type || '';
  const price = plan.price || 0;
  if (at === 'purchase') return { audience: 'public', pricing: 'one_time_purchase' };
  if (at === 'subscription') return price > 0 ? { audience: 'subscribers', pricing: 'one_time_purchase' } : { audience: 'subscribers', pricing: 'free' };
  if (at === 'public') return price > 0 ? { audience: 'public', pricing: 'one_time_purchase' } : { audience: 'public', pricing: 'free' };
  return price > 0 ? { audience: 'public', pricing: 'one_time_purchase' } : { audience: 'public', pricing: 'free' };
};

// =============================================================================
// MEAL PLANS TAB (marketplace / dashboard)
// =============================================================================
// ── Bookmark Button ────────────────────────────────────────────────────────
const MealBookmarkBtn = ({ productId, className = '' }) => {
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    const check = async () => {
      try {
        const res = await axios.get(`${API}/bookmarks/status/meal/${productId}`);
        setSaved(res.data.saved);
      } catch {}
    };
    check();
  }, [productId]);

  const toggle = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      if (saved) {
        await axios.post(`${API}/bookmarks/remove`, { product_id: productId, product_type: 'meal' });
        setSaved(false);
        toast.success('Removed from saved');
      } else {
        await axios.post(`${API}/bookmarks/save`, { product_id: productId, product_type: 'meal' });
        setSaved(true);
        toast.success('Saved to My Library');
      }
    } catch { toast.error('Failed to update bookmark'); }
    setBusy(false);
  };

  return (
    <button
      onClick={toggle}
      disabled={busy}
      className={`p-1.5 rounded-full transition-colors ${saved ? 'text-green-600 bg-green-50 hover:bg-green-100' : 'text-gray-400 hover:text-green-500 hover:bg-gray-100'} ${className}`}
      data-testid={`bookmark-meal-${productId}`}
      title={saved ? 'Remove from saved' : 'Save to My Meals'}
    >
      <Bookmark className={`h-4 w-4 ${saved ? 'fill-green-600' : ''}`} />
    </button>
  );
};

export const MealPlansTab = ({ mealPlans }) => {
  const [selectedPlan, setSelectedPlan] = useState(null);

  const getDietaryColor = (restrictions) => {
    if (!restrictions?.length) return 'bg-gray-100 text-gray-700';
    const r = restrictions[0]?.toLowerCase();
    if (r?.includes('vegan')) return 'bg-green-100 text-green-700';
    if (r?.includes('keto')) return 'bg-purple-100 text-purple-700';
    if (r?.includes('vegetarian')) return 'bg-orange-100 text-orange-700';
    return 'bg-blue-100 text-blue-700';
  };

  if (mealPlans.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Apple className="h-8 w-8 text-white" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">No meal plans available</h3>
        <p className="text-gray-600">Check back later for new meal plans from creators!</p>
      </div>
    );
  }

  return (
    <>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {mealPlans.map((plan) => {
          const isLocked = plan.is_locked;
          const { audience, pricing } = getAccessModel(plan);
          const lockRequires = plan.lock_requires || '';
          const description = plan.description || plan.description_preview || '';

          return (
            <Card key={plan.id} className={`group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 ${isLocked ? 'border-gray-200 opacity-95' : 'border-green-100 cursor-pointer'}`}
              onClick={() => !isLocked && setSelectedPlan(plan)}
              data-testid={`meal-card-${plan.id}`}
            >
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-2 min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {(plan.dietary_restrictions || []).length > 0 && (
                        <Badge className={`${getDietaryColor(plan.dietary_restrictions)} px-2 py-1`}>
                          <Leaf className="h-3 w-3 mr-1" />{plan.dietary_restrictions[0]}
                        </Badge>
                      )}
                      {audience === 'subscribers' && (
                        <Badge data-testid={`meal-audience-badge-${plan.id}`} className="bg-amber-100 text-amber-700 px-2 py-1"><Lock className="h-3 w-3 mr-1" />Subscribers</Badge>
                      )}
                      {pricing === 'one_time_purchase' && (
                        <Badge data-testid={`meal-pricing-badge-${plan.id}`} className="bg-blue-100 text-blue-700 px-2 py-1"><DollarSign className="h-3 w-3 mr-1" />${plan.price}</Badge>
                      )}
                      {!isLocked && (audience !== 'public' || pricing !== 'free') && (
                        <Badge className="bg-emerald-100 text-emerald-700 px-2 py-1"><Unlock className="h-3 w-3 mr-1" />Unlocked</Badge>
                      )}
                    </div>
                    <CardTitle className="text-lg line-clamp-2">{plan.title}</CardTitle>
                    {plan.creator_name && (
                      <p className="text-xs text-gray-500 mt-0.5">by {plan.creator_name}</p>
                    )}
                  </div>
                  <div className="text-right shrink-0 flex flex-col items-end gap-1">
                    <MealBookmarkBtn productId={plan.id} />
                    {pricing === 'one_time_purchase' && <div className="text-xl font-bold text-green-600 whitespace-nowrap">${plan.price}</div>}
                    {pricing === 'free' && <div className="text-base font-bold text-green-600">Free</div>}
                    <div className="text-xs text-gray-500 whitespace-nowrap">{plan.purchases_count || 0} sales</div>
                  </div>
                </div>

                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <div className="flex items-center space-x-1"><Calendar className="h-4 w-4" /><span>{plan.duration_days} days</span></div>
                  {plan.daily_calories && <div className="flex items-center space-x-1"><Calculator className="h-4 w-4" /><span>{plan.daily_calories} cal/day</span></div>}
                </div>
              </CardHeader>

              <CardContent>
                <div className="space-y-4">
                  <p className="text-gray-700 text-sm line-clamp-3">{description}</p>

                  {!isLocked && plan.meals && (
                    <div className="pt-2 border-t flex items-center justify-between text-sm">
                      <div className="flex items-center space-x-1 text-gray-600">
                        <Utensils className="h-4 w-4" /><span>{(plan.meals || []).length} meals</span>
                        {(plan.meals || []).some(m => m.video_url) && (
                          <span className="text-green-500 ml-2 flex items-center gap-0.5"><Activity className="h-3 w-3" /> Videos</span>
                        )}
                        {(plan.meals || []).some(m => m.photo_url) && !((plan.meals || []).some(m => m.video_url)) && (
                          <span className="text-green-500 ml-2 flex items-center gap-0.5"><Camera className="h-3 w-3" /> Photos</span>
                        )}
                      </div>
                      <div className="text-xs text-green-500 font-medium">View Plan &rarr;</div>
                    </div>
                  )}

                  {isLocked && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-gray-50 border border-gray-200">
                      <Lock className="h-4 w-4 text-gray-400 shrink-0" />
                      <span className="text-sm text-gray-500">{plan.lock_message || 'Full details locked'}</span>
                    </div>
                  )}

                  {isLocked && lockRequires === 'subscription' && (
                    <div onClick={(e) => e.stopPropagation()}>
                      <Button data-testid={`meal-subscribe-cta-${plan.id}`} className="w-full bg-amber-500 hover:bg-amber-600 text-white" onClick={() => window.location.href = `/profile/${plan.creator_id}`}>
                        <Lock className="h-4 w-4 mr-2" />Subscribe to Access
                      </Button>
                    </div>
                  )}
                  {isLocked && lockRequires === 'purchase' && (
                    <div onClick={(e) => e.stopPropagation()}>
                      <PaymentButton data-testid={`meal-buy-cta-${plan.id}`} itemType="meal_plan" itemId={plan.id} price={plan.price} title={plan.title} description={description} className="w-full">
                        Buy ${plan.price}
                      </PaymentButton>
                    </div>
                  )}
                  {isLocked && lockRequires === 'subscription_and_purchase' && (
                    <div onClick={(e) => e.stopPropagation()} className="space-y-2">
                      <Button data-testid={`meal-subscribe-cta-${plan.id}`} className="w-full bg-amber-500 hover:bg-amber-600 text-white" onClick={() => window.location.href = `/profile/${plan.creator_id}`}>
                        <Lock className="h-4 w-4 mr-2" />Subscribe + Buy ${plan.price}
                      </Button>
                      <p className="text-xs text-center text-gray-500">Requires active subscription and one-time purchase</p>
                    </div>
                  )}
                  {!isLocked && audience === 'public' && pricing === 'free' && (
                    <div className="text-xs text-center text-green-600 font-medium py-1">
                      <Unlock className="h-3 w-3 inline mr-1" />Free Access — Click to view
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Detail View */}
      <MealPlanDetailView plan={selectedPlan} open={!!selectedPlan} onClose={() => setSelectedPlan(null)} />
    </>
  );
};


// =============================================================================
// MY MEAL PLANS TAB (purchased + saved + subscription-access)
// =============================================================================
export const MyMealPlansTab = ({ myMealPlans }) => {
  const [selectedPlan, setSelectedPlan] = useState(null);

  if (myMealPlans.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Bookmark className="h-8 w-8 text-white" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">No meal plans yet</h3>
        <p className="text-gray-600">Save or purchase meal plans to build your library!</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {myMealPlans.map((plan) => {
          const isAccessible = plan.is_accessible !== false;
          const isLocked = plan.is_locked || !isAccessible;
          const isPurchased = plan.is_purchased;
          const isSaved = plan.is_saved;
          const hasSub = plan.has_subscription_access;
          const description = plan.description || plan.description_preview || '';

          return (
            <Card
              key={plan.id}
              className={`group hover:shadow-lg transition-all duration-300 ${isLocked ? 'border-gray-200 opacity-90' : 'border-green-100 cursor-pointer'}`}
              onClick={() => isAccessible && setSelectedPlan(plan)}
              data-testid={`my-meal-card-${plan.id}`}
            >
              <CardHeader>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {isPurchased && (
                      <Badge className="bg-green-100 text-green-700 px-2 py-1">
                        <Unlock className="h-3 w-3 mr-1" />Purchased
                      </Badge>
                    )}
                    {isSaved && (
                      <Badge className="bg-blue-100 text-blue-700 px-2 py-1">
                        <Bookmark className="h-3 w-3 mr-1 fill-blue-700" />Saved
                      </Badge>
                    )}
                    {hasSub && !isPurchased && isAccessible && (
                      <Badge className="bg-purple-100 text-purple-700 px-2 py-1">
                        <Star className="h-3 w-3 mr-1" />Subscription
                      </Badge>
                    )}
                    {isLocked && (
                      <Badge className="bg-gray-100 text-gray-500 px-2 py-1">
                        <Lock className="h-3 w-3 mr-1" />Locked
                      </Badge>
                    )}
                  </div>
                  {(plan.dietary_restrictions || []).length > 0 && (
                    <Badge variant="outline" className="capitalize">{plan.dietary_restrictions[0]}</Badge>
                  )}
                </div>
                <CardTitle className="text-lg line-clamp-2">{plan.title}</CardTitle>
                {plan.creator_name && (
                  <p className="text-xs text-gray-500">by {plan.creator_name}</p>
                )}
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p className="text-gray-700 text-sm line-clamp-2">{description}</p>
                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    {plan.duration_days && (
                      <div className="flex items-center space-x-1"><Calendar className="h-4 w-4" /><span>{plan.duration_days} days</span></div>
                    )}
                    {plan.meals && (
                      <div className="flex items-center space-x-1"><Utensils className="h-4 w-4" /><span>{(plan.meals || []).length} meals</span></div>
                    )}
                  </div>

                  {isLocked && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
                      <Lock className="h-4 w-4 text-amber-500 shrink-0" />
                      <span className="text-sm text-amber-700">
                        {plan.lock_message || 'Subscribe to unlock this content'}
                      </span>
                    </div>
                  )}

                  {isLocked && plan.creator_id && (
                    <div onClick={(e) => e.stopPropagation()}>
                      <Button
                        className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                        onClick={() => window.location.href = `/profile/${plan.creator_id}`}
                        data-testid={`my-meal-subscribe-cta-${plan.id}`}
                      >
                        <Lock className="h-4 w-4 mr-2" />Subscribe to Unlock
                      </Button>
                    </div>
                  )}

                  {isAccessible && (
                    <div className="text-xs text-center text-green-500 font-medium pt-1">
                      Click to view full plan &rarr;
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Detail View */}
      <MealPlanDetailView plan={selectedPlan} open={!!selectedPlan} onClose={() => setSelectedPlan(null)} />
    </div>
  );
};
